import type { Point2D } from '@shared/models/geometry.model';
import type { Storey } from '@shared/models/design/storey.model';
import { computePolygonArea } from '@shared/helpers/geometry/geometry.helpers';
import {
  findInfiniteLineIntersection,
  shapePointsExceedMaxSeparation,
} from '@shared/helpers/geometry/point-separation.helpers';
import { isPolygonSelfIntersecting } from '@shared/helpers/geometry/polygon.helpers';

const DIRECTION_EPSILON_METERS = 1e-6;
const MIN_DIRECTION_SYSTEM_EIGENVALUE_RATIO = 1e-6;
const MAX_EDITED_SHAPE_AREA_SQUARE_METERS = 100_000;
const MAX_POINT_DISPLACEMENT_SCALE = 10;
export const MIN_EDITABLE_SEGMENT_LENGTH_METERS = 0.1;

export type CommittedSegmentResizePlan = {
  startIndex: number;
  endIndex: number;
  nextStartPoint: Point2D;
  nextEndPoint: Point2D;
  nextPointPositions: ReadonlyMap<number, Point2D>;
};

export type ManualSegmentLengthTimestamps = ReadonlyMap<number, number>;

export type StoreySegmentReference = {
  shapeId: string;
  startIndex: number;
  endIndex: number;
  lengthMeters: number;
};

/** Find the longest segment across every solid and cutout in a storey. */
export const getLargestStoreySegment = (
  storey: Storey | undefined,
): StoreySegmentReference | undefined => {
  let largestSegment: StoreySegmentReference | undefined;

  for (const shape of storey?.shapes ?? []) {
    for (
      let startIndex = 0;
      startIndex < shape.points.length;
      startIndex += 1
    ) {
      const endIndex = (startIndex + 1) % shape.points.length;
      const start = shape.points[startIndex];
      const end = shape.points[endIndex];
      if (!start || !end) continue;
      const lengthMeters = Math.hypot(end[0] - start[0], end[1] - start[1]);
      if (
        Number.isFinite(lengthMeters) &&
        lengthMeters > 0 &&
        (!largestSegment || lengthMeters > largestSegment.lengthMeters)
      ) {
        largestSegment = {
          shapeId: shape.id,
          startIndex,
          endIndex,
          lengthMeters,
        };
      }
    }
  }

  return largestSegment;
};

/**
 * Format segment length for map labels (same rules as draft tooltips).
 */
export const formatSegmentLengthMeters = (
  value: number,
): string | undefined => {
  if (!Number.isFinite(value)) return undefined;
  return value.toFixed(1).replace(/\.0$/, '');
};

/** Compact blur-state label; focus restores the saved one-decimal value. */
export const formatDisplayedSegmentLengthMeters = (
  value: number,
): string | undefined =>
  Number.isFinite(value) ? String(Math.round(value)) : undefined;

/** Parse an editable segment length while enforcing the geometry minimum. */
export const parseSegmentLengthInput = (value: string): number | undefined => {
  if (value.trim() === '') return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= MIN_EDITABLE_SEGMENT_LENGTH_METERS
    ? parsed
    : undefined;
};

/** Keep segment input numeric and display a period regardless of browser locale. */
export const normalizeSegmentLengthInput = (
  value: string,
): string | undefined => {
  const normalized = value.replaceAll(',', '.');
  return /^\d*(?:\.\d*)?$/.test(normalized) ? normalized : undefined;
};

const distance = (first: Point2D, second: Point2D): number =>
  Math.hypot(second[0] - first[0], second[1] - first[1]);

/**
 * Resize one committed edge while preserving every polygon edge direction.
 * The two neighboring supporting lines are translated parallel to themselves;
 * their outer endpoints are re-intersected with the unchanged outer lines.
 * The length delta is split evenly, unless one neighbor would become too short.
 */
export const planCommittedSegmentResize = (input: {
  points: readonly Point2D[];
  startIndex: number;
  endIndex: number;
  lengthMeters: number;
  minSegmentLengthMeters?: number;
  /** Manual edit timestamps keyed by segment start index. */
  manualSegmentTimestamps?: ManualSegmentLengthTimestamps;
}): CommittedSegmentResizePlan | undefined => {
  const minimum =
    input.minSegmentLengthMeters ?? MIN_EDITABLE_SEGMENT_LENGTH_METERS;
  if (
    !Number.isFinite(input.lengthMeters) ||
    input.lengthMeters < minimum ||
    input.points.length < 3 ||
    (input.startIndex + 1) % input.points.length !== input.endIndex
  ) {
    return undefined;
  }

  const start = input.points[input.startIndex];
  const end = input.points[input.endIndex];
  const previous =
    input.points[
      (input.startIndex - 1 + input.points.length) % input.points.length
    ];
  const next = input.points[(input.endIndex + 1) % input.points.length];
  if (!start || !end || !previous || !next) return undefined;

  const dx = end[0] - start[0];
  const dy = end[1] - start[1];
  const currentLength = Math.hypot(dx, dy);
  if (!(currentLength > DIRECTION_EPSILON_METERS)) return undefined;

  const unitX = dx / currentLength;
  const unitY = dy / currentLength;
  const delta = input.lengthMeters - currentLength;
  const referenceLength = Math.max(
    1,
    input.lengthMeters,
    ...input.points.map((point, index) =>
      distance(point, input.points[(index + 1) % input.points.length] ?? point),
    ),
  );

  const isReasonablePlan = (plan: CommittedSegmentResizePlan): boolean => {
    const nextPoints = input.points.map(
      (point, index) => plan.nextPointPositions.get(index) ?? point,
    );
    return (
      nextPoints.every((point) => point.every(Number.isFinite)) &&
      !shapePointsExceedMaxSeparation(nextPoints) &&
      computePolygonArea(nextPoints) <= MAX_EDITED_SHAPE_AREA_SQUARE_METERS &&
      nextPoints.every(
        (point, index) =>
          distance(point, input.points[index] ?? point) <=
          referenceLength * MAX_POINT_DISPLACEMENT_SCALE,
      )
    );
  };

  const previousIndex =
    (input.startIndex - 1 + input.points.length) % input.points.length;
  const nextIndex = (input.endIndex + 1) % input.points.length;
  const previousPreviousIndex =
    (previousIndex - 1 + input.points.length) % input.points.length;
  const nextNextIndex = (nextIndex + 1) % input.points.length;
  const previousPrevious = input.points[previousPreviousIndex];
  const nextNext = input.points[nextNextIndex];
  if (!previousPrevious || !nextNext) return undefined;

  const buildCandidate = (
    startShare: number,
  ):
    | {
        plan: CommittedSegmentResizePlan;
        previousIsValid: boolean;
        nextIsValid: boolean;
      }
    | undefined => {
    const endShare = delta - startShare;
    const nextStartPoint: Point2D = [
      start[0] - unitX * startShare,
      start[1] - unitY * startShare,
    ];
    const nextEndPoint: Point2D = [
      end[0] + unitX * endShare,
      end[1] + unitY * endShare,
    ];

    let nextPreviousPoint: Point2D | undefined;
    let nextNextPoint: Point2D | undefined;
    if (previousIndex === nextIndex) {
      const sharedPoint = findInfiniteLineIntersection(
        nextStartPoint,
        [
          nextStartPoint[0] + start[0] - previous[0],
          nextStartPoint[1] + start[1] - previous[1],
        ],
        nextEndPoint,
        [
          nextEndPoint[0] + next[0] - end[0],
          nextEndPoint[1] + next[1] - end[1],
        ],
      );
      nextPreviousPoint = sharedPoint;
      nextNextPoint = sharedPoint;
    } else {
      nextPreviousPoint = findInfiniteLineIntersection(
        previousPrevious,
        previous,
        nextStartPoint,
        [
          nextStartPoint[0] + start[0] - previous[0],
          nextStartPoint[1] + start[1] - previous[1],
        ],
      );
      nextNextPoint = findInfiniteLineIntersection(
        nextEndPoint,
        [
          nextEndPoint[0] + next[0] - end[0],
          nextEndPoint[1] + next[1] - end[1],
        ],
        next,
        nextNext,
      );
    }
    if (!nextPreviousPoint || !nextNextPoint) return undefined;
    return {
      plan: {
        startIndex: input.startIndex,
        endIndex: input.endIndex,
        nextStartPoint,
        nextEndPoint,
        nextPointPositions: new Map([
          [previousIndex, nextPreviousPoint],
          [input.startIndex, nextStartPoint],
          [input.endIndex, nextEndPoint],
          [nextIndex, nextNextPoint],
        ]),
      },
      previousIsValid: distance(nextPreviousPoint, nextStartPoint) >= minimum,
      nextIsValid: distance(nextEndPoint, nextNextPoint) >= minimum,
    };
  };

  const manualSegmentsOldestFirst = [...(input.manualSegmentTimestamps ?? [])]
    .filter(
      ([segmentIndex, timestamp]) =>
        segmentIndex >= 0 &&
        segmentIndex < input.points.length &&
        segmentIndex !== input.startIndex &&
        Number.isFinite(timestamp),
    )
    // Relax the oldest manual lock first when the full constraint set fails.
    .sort((left, right) => left[1] - right[1]);

  const symmetric = buildCandidate(delta / 2);
  const localPlan = (() => {
    if (!symmetric) return undefined;
    if (symmetric.previousIsValid && symmetric.nextIsValid) {
      return symmetric.plan;
    }

    // Keep the constrained neighboring line fixed and let the other side absorb
    // the entire change. If both sides are constrained, the edit is impossible.
    const fallbackStartShare =
      !symmetric.previousIsValid && symmetric.nextIsValid
        ? 0
        : symmetric.previousIsValid && !symmetric.nextIsValid
          ? delta
          : undefined;
    if (fallbackStartShare === undefined) return undefined;
    const fallback = buildCandidate(fallbackStartShare);
    return fallback?.previousIsValid && fallback.nextIsValid
      ? fallback.plan
      : undefined;
  })();
  const reasonableLocalPlan =
    localPlan && isReasonablePlan(localPlan) ? localPlan : undefined;

  if (manualSegmentsOldestFirst.length === 0) return reasonableLocalPlan;

  const getPlanSegmentLength = (
    plan: CommittedSegmentResizePlan,
    segmentIndex: number,
  ): number | undefined => {
    const startPoint =
      plan.nextPointPositions.get(segmentIndex) ?? input.points[segmentIndex];
    const endIndex = (segmentIndex + 1) % input.points.length;
    const endPoint =
      plan.nextPointPositions.get(endIndex) ?? input.points[endIndex];
    return startPoint && endPoint ? distance(startPoint, endPoint) : undefined;
  };

  if (
    reasonableLocalPlan &&
    manualSegmentsOldestFirst.every(([segmentIndex]) => {
      const originalStart = input.points[segmentIndex];
      const originalEnd =
        input.points[(segmentIndex + 1) % input.points.length];
      const plannedLength = getPlanSegmentLength(
        reasonableLocalPlan,
        segmentIndex,
      );
      return Boolean(
        originalStart &&
        originalEnd &&
        plannedLength !== undefined &&
        Math.abs(plannedLength - distance(originalStart, originalEnd)) <= 1e-6,
      );
    })
  ) {
    return reasonableLocalPlan;
  }

  const originalLengths: number[] = [];
  const directions: Point2D[] = [];
  for (let index = 0; index < input.points.length; index += 1) {
    const edgeStart = input.points[index];
    const edgeEnd = input.points[(index + 1) % input.points.length];
    if (!edgeStart || !edgeEnd) return reasonableLocalPlan;
    const edgeLength = distance(edgeStart, edgeEnd);
    if (!(edgeLength > DIRECTION_EPSILON_METERS)) {
      return reasonableLocalPlan;
    }
    originalLengths.push(edgeLength);
    directions.push([
      (edgeEnd[0] - edgeStart[0]) / edgeLength,
      (edgeEnd[1] - edgeStart[1]) / edgeLength,
    ]);
  }

  const solveWithManualLocks = (
    lockedManualIndexes: ReadonlySet<number>,
  ): CommittedSegmentResizePlan | undefined => {
    const fixedDeltas = new Map<number, number>([
      [input.startIndex, input.lengthMeters - currentLength],
    ]);
    lockedManualIndexes.forEach((index) => fixedDeltas.set(index, 0));

    let solvedDeltas: number[] | undefined;
    for (let iteration = 0; iteration <= input.points.length; iteration += 1) {
      const freeIndexes = originalLengths.flatMap((_, index) =>
        fixedDeltas.has(index) ? [] : [index],
      );
      const fixedOffset = [...fixedDeltas].reduce<Point2D>(
        (sum, [index, fixedDelta]) => {
          const direction = directions[index];
          return direction
            ? [
                sum[0] + fixedDelta * direction[0],
                sum[1] + fixedDelta * direction[1],
              ]
            : sum;
        },
        [0, 0],
      );
      const targetOffset: Point2D = [-fixedOffset[0], -fixedOffset[1]];

      let m00 = 0;
      let m01 = 0;
      let m11 = 0;
      for (const index of freeIndexes) {
        const direction = directions[index];
        if (!direction) continue;
        m00 += direction[0] * direction[0];
        m01 += direction[0] * direction[1];
        m11 += direction[1] * direction[1];
      }

      let lambdaX = 0;
      let lambdaY = 0;
      const determinant = m00 * m11 - m01 * m01;
      const trace = m00 + m11;
      const discriminant = Math.sqrt(
        Math.max(0, (m00 - m11) ** 2 + 4 * m01 ** 2),
      );
      const largestEigenvalue = (trace + discriminant) / 2;
      const smallestEigenvalue = (trace - discriminant) / 2;
      const hasFullRank = Math.abs(determinant) > DIRECTION_EPSILON_METERS ** 2;
      if (
        hasFullRank &&
        largestEigenvalue > DIRECTION_EPSILON_METERS &&
        smallestEigenvalue / largestEigenvalue <
          MIN_DIRECTION_SYSTEM_EIGENVALUE_RATIO
      ) {
        return undefined;
      }
      if (hasFullRank) {
        lambdaX = (targetOffset[0] * m11 - targetOffset[1] * m01) / determinant;
        lambdaY = (targetOffset[1] * m00 - targetOffset[0] * m01) / determinant;
      } else if (m00 >= m11 && m00 > DIRECTION_EPSILON_METERS) {
        lambdaX = targetOffset[0] / m00;
      } else if (m11 > DIRECTION_EPSILON_METERS) {
        lambdaY = targetOffset[1] / m11;
      } else if (Math.hypot(...targetOffset) > 1e-6) {
        return undefined;
      }

      const deltas = originalLengths.map(
        (_, index) => fixedDeltas.get(index) ?? 0,
      );
      for (const index of freeIndexes) {
        const direction = directions[index];
        if (!direction) continue;
        deltas[index] = direction[0] * lambdaX + direction[1] * lambdaY;
      }

      const residual = deltas.reduce<Point2D>(
        (sum, edgeDelta, index) => {
          const direction = directions[index];
          return direction
            ? [
                sum[0] + edgeDelta * direction[0],
                sum[1] + edgeDelta * direction[1],
              ]
            : sum;
        },
        [0, 0],
      );
      if (Math.hypot(...residual) > 1e-6) return undefined;

      const shortest = freeIndexes.reduce<
        { index: number; length: number } | undefined
      >((result, index) => {
        const length = (originalLengths[index] ?? 0) + (deltas[index] ?? 0);
        return !result || length < result.length ? { index, length } : result;
      }, undefined);
      if (shortest && shortest.length < minimum - 1e-6) {
        fixedDeltas.set(
          shortest.index,
          minimum - (originalLengths[shortest.index] ?? 0),
        );
        continue;
      }

      solvedDeltas = deltas;
      break;
    }
    if (!solvedDeltas) return undefined;

    const nextLengths = originalLengths.map(
      (length, index) => length + (solvedDeltas[index] ?? 0),
    );
    const relativePoints: Point2D[] = [[0, 0]];
    for (let index = 0; index < input.points.length - 1; index += 1) {
      const relativeStart = relativePoints[index];
      const direction = directions[index];
      const nextLength = nextLengths[index];
      if (!relativeStart || !direction || nextLength === undefined) {
        return undefined;
      }
      relativePoints.push([
        relativeStart[0] + direction[0] * nextLength,
        relativeStart[1] + direction[1] * nextLength,
      ]);
    }

    const translation = input.points.reduce<Point2D>(
      (sum, point, index) => {
        const relativePoint = relativePoints[index];
        return relativePoint
          ? [
              sum[0] + point[0] - relativePoint[0],
              sum[1] + point[1] - relativePoint[1],
            ]
          : sum;
      },
      [0, 0],
    );
    const averageTranslation: Point2D = [
      translation[0] / input.points.length,
      translation[1] / input.points.length,
    ];

    const nextPoints = relativePoints.map((point): Point2D => [
      point[0] + averageTranslation[0],
      point[1] + averageTranslation[1],
    ]);
    if (isPolygonSelfIntersecting(nextPoints)) return undefined;
    const nextStartPoint = nextPoints[input.startIndex];
    const nextEndPoint = nextPoints[input.endIndex];
    if (!nextStartPoint || !nextEndPoint) return undefined;
    const plan: CommittedSegmentResizePlan = {
      startIndex: input.startIndex,
      endIndex: input.endIndex,
      nextStartPoint,
      nextEndPoint,
      nextPointPositions: new Map(
        nextPoints.map((point, index) => [index, point]),
      ),
    };
    return isReasonablePlan(plan) ? plan : undefined;
  };

  const retainedManualIndexes = new Set(
    manualSegmentsOldestFirst.map(([segmentIndex]) => segmentIndex),
  );
  for (const [oldestIndex] of manualSegmentsOldestFirst) {
    const plan = solveWithManualLocks(retainedManualIndexes);
    if (plan) return plan;
    retainedManualIndexes.delete(oldestIndex);
  }

  return reasonableLocalPlan;
};
